import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, Clock, LogOut, ChevronDown, CheckCircle2, XCircle, CalendarIcon, Navigation, CalendarOff, User, Download, X, DoorOpen, Briefcase, HelpCircle, Info, } from "lucide-react";
import FingerprintIcon from "@/components/ui/FingerprintIcon.tsx";
import { toast } from "sonner";
import { format, startOfWeek, isToday } from "date-fns";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";
import EmployeeGroupSchedule from "@/components/EmployeeGroupSchedule";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  
  const [user, setUser] = useState(location.state?.user || null);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [geofenceStatus, setGeofenceStatus] = useState(null);
  const [isCheckingGeofence, setIsCheckingGeofence] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isTouchDevice = window.matchMedia("(hover: none)").matches;
  const [isLoading, setIsLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [companyName, setCompanyName] = useState("");
  const [companyPayType, setCompanyPayType] = useState("hourly");
  const [permanentSchedule, setPermanentSchedule] = useState([]);
  const [todayShift, setTodayShift] = useState(null);
  
  // Time off
  const [timeOffDate, setTimeOffDate] = useState(new Date());
  const [timeOffEndDate, setTimeOffEndDate] = useState(new Date());
  const [timeOffMode, setTimeOffMode] = useState("single"); // "single" or "range"
  const [timeOffExcludedDays, setTimeOffExcludedDays] = useState([]);
  const [timeOffReason, setTimeOffReason] = useState("");
  const [timeOffDetails, setTimeOffDetails] = useState("");
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [isSubmittingTimeOff, setIsSubmittingTimeOff] = useState(false);
  
  // Profile
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  
  // Shift scheduling
  const [shiftDate, setShiftDate] = useState(new Date());
  const [shiftStartTime, setShiftStartTime] = useState("09:00");
  const [shiftEndTime, setShiftEndTime] = useState("17:00");
  const [shiftNotes, setShiftNotes] = useState("");
  const [myShiftRequests, setMyShiftRequests] = useState([]);
  const [justSubmittedShift, setJustSubmittedShift] = useState(null);
  
  // Export
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(new Date());
  const [exportEndDate, setExportEndDate] = useState(new Date());

  // Late reason dialog
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [lateReason, setLateReason] = useState("");
  const [pendingCheckInLocation, setPendingCheckInLocation] = useState(null);

  // Multi-day shift request
  const [shiftRequestMode, setShiftRequestMode] = useState("single"); // "single" or "range"
  const [shiftEndDate, setShiftEndDate] = useState(new Date());
  const [excludedDays, setExcludedDays] = useState([]);
  
  // Subscription status
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(() => localStorage.getItem('trialBannerDismissed') === 'true');

  const dismissBanner = () => {
    setBannerDismissed(true);
    localStorage.setItem('trialBannerDismissed', 'true');
  };

  const dayNames = [t("monday") || "Mon", t("tuesday") || "Tue", t("wednesday") || "Wed", t("thursday") || "Thu", t("friday") || "Fri", t("saturday") || "Sat", t("sunday") || "Sun"];

  useEffect(() => {
    if (!user) {
      const fetchUser = async () => {
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
          if (res.ok) { const data = await res.json(); setUser(data); setDisplayName(data.display_name || data.name || ""); }
        } catch (e) { console.error(e); }
      };
      fetchUser();
    } else {
      setDisplayName(user.display_name || user.name || "");
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    try {
      const statusRes = await fetch(`${API_URL}/api/my-status`, { credentials: 'include' });
      if (statusRes.ok) { 
        const data = await statusRes.json(); 
        setIsCheckedIn(data.is_checked_in); 
        setCompanyName(data.company_name || ""); 
        setCompanyPayType(data.company_pay_type || "hourly");
        if (currentLocation) {
          fetchGeofenceStatus(currentLocation);
        }
      }

      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const historyRes = await fetch(`${API_URL}/api/my-history?date=${dateStr}`, { credentials: 'include' });
      if (historyRes.ok) setHistory(await historyRes.json());
      
      const timeOffRes = await fetch(`${API_URL}/api/my-time-off`, { credentials: 'include' });
      if (timeOffRes.ok) setTimeOffRequests(await timeOffRes.json());
      
      const shiftReqRes = await fetch(`${API_URL}/api/shifts/my-requests`, { credentials: 'include' });
      if (shiftReqRes.ok) setMyShiftRequests(await shiftReqRes.json());
      
      const subRes = await fetch(`${API_URL}/api/subscription/status`, { credentials: 'include' });
      if (subRes.ok) setSubscriptionStatus(await subRes.json());
      
      // Fetch permanent schedule for salary companies
      const permScheduleRes = await fetch(`${API_URL}/api/schedule/my-permanent`, { credentials: 'include' });
      if (permScheduleRes.ok) setPermanentSchedule((await permScheduleRes.json()).schedule || []);
      
      // Get today's shift info for context display
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const weekScheduleRes = await fetch(`${API_URL}/api/schedule/week?week_start=${format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')}`, { credentials: 'include' });
      if (weekScheduleRes.ok) {
        const weekData = await weekScheduleRes.json();
        const todayData = weekData.schedule?.[todayStr];
        // Find this user's shift/permanent schedule for today
        if (todayData) {
          const myPerm = todayData.permanent?.find(p => p.user_id === user?.user_id);
          const myShift = todayData.shifts?.find(s => s.user_id === user?.user_id);
          setTodayShift(myShift || myPerm || null);
        }
      }
    } catch (e) { console.error(e); }
  }, [selectedDate, user?.user_id, currentLocation, fetchGeofenceStatus]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

useEffect(() => {
  const updateLoc = async () => {
    try {
      const loc = await getCurrentLocation();
      setCurrentLocation(loc);
      setLocationError(null);
      fetchGeofenceStatus(loc);
    } catch (e) {
      setLocationError(t("locationDenied"));
    }
  };
  updateLoc();
}, [t, fetchGeofenceStatus]);
    const fetchGeofenceStatus = useCallback(async (locOverride = null) => {
      try {
        setIsCheckingGeofence(true);

        let loc = locOverride || currentLocation;
        if (!loc) {
          loc = await getCurrentLocation();
          setCurrentLocation(loc);
        }

        const params = new URLSearchParams({
          latitude: String(loc.latitude),
          longitude: String(loc.longitude),
          accuracy: String(loc.accuracy ?? 9999),
        });

        const res = await fetch(`${API_URL}/api/geofencing/my-status?${params.toString()}`, {
          credentials: "include",
        });

        if (res.ok) {
          const data = await res.json();
          setGeofenceStatus(data);
        } else {
          setGeofenceStatus(null);
        }
      } catch (e) {
        console.error(e);
        setGeofenceStatus(null);
      } finally {
        setIsCheckingGeofence(false);
      }
    }, [currentLocation]);
  
  const handleCheckIn = async (providedLateReason = null) => {
    setIsLoading(true);
    if (geofenceStatus?.geofencing_enabled) {
      if (!geofenceStatus?.has_assignments) {
        setIsLoading(false);
        toast.error(t("geofenceNoAssignment") || "Your employer must assign a work location first.");
        return;
      }

      if (!geofenceStatus?.can_check_in) {
        setIsLoading(false);
        toast.error(
          geofenceStatus?.message ||
          (t("geofenceOutside") || "You are outside the allowed check-in area.")
        );
        return;
      }
    }
    try {
     const loc = pendingCheckInLocation || currentLocation;
      if (!loc) {
        throw new Error("Location not ready yet");
      }
      const locationData = {
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy ?? null,
        address: `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`,
        late_reason: providedLateReason || null
      };
      const res = await fetch(`${API_URL}/api/check-in`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        credentials: 'include',
        body: JSON.stringify(locationData)
      });
      
      // Read response body only once
      const responseText = await res.text();
      let data;
      try { 
        data = JSON.parse(responseText);
      } catch { 
        throw new Error(responseText || "Check-in failed"); 
      }
      if (!res.ok) { throw new Error(data.detail || "Check-in failed"); }
      
      // If late and no reason provided yet, show dialog
      if (data.is_late && !providedLateReason && !data.reason_updated) {
        setPendingCheckInLocation({ latitude: loc.latitude, longitude: loc.longitude });
        setShowLateReasonDialog(true);
        // Still set as checked in since backend already created the record
        setIsCheckedIn(true);
        if (data.check) {
          setHistory(prev => [data.check, ...prev]);
        }
        setIsLoading(false);
        return;
      }
      
      toast.success(data.reason_updated ? (t("lateReasonUpdated") || "Late reason submitted") : t("checkInSuccess"));
      if (data.is_late && !data.reason_updated) toast.warning(t("late"));
      if (data.flag_reason?.includes("ip")) toast.info(t("flaggedIP") || "Flagged: Same IP as other employee");
      
      // Update states
      setIsCheckedIn(true);
      setPendingCheckInLocation(null);
      
      // Add new check-in to history immediately (only if not updating reason)
      if (data.check && !data.reason_updated) {
        setHistory(prev => [data.check, ...prev]);
      }
    } catch (e) { toast.error(e.message); }
    finally { setIsLoading(false); }
  };

  const handleLateReasonSubmit = async () => {
    setShowLateReasonDialog(false);
    await handleCheckIn(lateReason);
    setLateReason("");
  };

  const handleCheckOut = async () => {
  setIsLoading(true);
  try {
    let loc = currentLocation;

    if (!loc) {
      loc = await getCurrentLocation();
    }
      const locationData = {
        latitude: loc.latitude,
        longitude: loc.longitude,
        address: `${loc.latitude.toFixed(6)}, ${loc.longitude.toFixed(6)}`
      };
      const res = await fetch(`${API_URL}/api/check-out`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }, 
        credentials: 'include',
        body: JSON.stringify(locationData)
      });
      
      // Read response body only once
      const responseText = await res.text();
      let data;
      try { 
        data = JSON.parse(responseText);
      } catch { 
        throw new Error(responseText || "Check-out failed"); 
      }
      if (!res.ok) { throw new Error(data.detail || "Check-out failed"); }
      
      toast.success(t("checkOutSuccess"));
      
      // Update states
      setIsCheckedIn(false);
      
      // Add new check-out to history immediately - this will persist
      if (data.check) {
        setHistory(prev => [data.check, ...prev]);
      }
      // Don't call fetchData - it would overwrite our state update due to timezone issues
    } catch (e) { toast.error(e.message); }
    finally { setIsLoading(false); }
  };

  const handleTimeOffSubmit = async () => {
    if (!timeOffReason) { toast.error("Please select a reason"); return; }
    setIsSubmittingTimeOff(true);
    try {
      const body = {
        reason_type: timeOffReason,
        reason_details: timeOffDetails || null
      };
      
      if (timeOffMode === "range") {
        body.start_date = format(timeOffDate, 'yyyy-MM-dd');
        body.end_date = format(timeOffEndDate, 'yyyy-MM-dd');
        body.exclude_days = timeOffExcludedDays.length > 0 ? timeOffExcludedDays : null;
      } else {
        body.date = format(timeOffDate, 'yyyy-MM-dd');
      }
      
      const res = await fetch(`${API_URL}/api/time-off`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail); }
      const data = await res.json();
      toast.success(data.message || t("requestSubmitted"));
      setTimeOffReason(""); setTimeOffDetails("");
      setTimeOffExcludedDays([]);
      fetchData();
    } catch (e) { toast.error(e.message); }
    finally { setIsSubmittingTimeOff(false); }
  };

  const toggleTimeOffExcludedDay = (dayIndex) => {
    setTimeOffExcludedDays(prev => 
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const handleShiftRequest = async () => {
    try {
      const body = {
        start_time: shiftStartTime, 
        end_time: shiftEndTime, 
        notes: shiftNotes || null
      };
      
      if (shiftRequestMode === "range") {
        body.start_date = format(shiftDate, 'yyyy-MM-dd');
        body.end_date = format(shiftEndDate, 'yyyy-MM-dd');
        body.exclude_days = excludedDays.length > 0 ? excludedDays : null;
      } else {
        body.date = format(shiftDate, 'yyyy-MM-dd');
      }
      
      const res = await fetch(`${API_URL}/api/shifts/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body)
      });
      
      // Read response body only once
      const responseText = await res.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error(responseText || "Request failed");
      }
      if (!res.ok) { throw new Error(data.detail || "Request failed"); }
      
      toast.success(data.message || t("requestSubmitted"));
      // Set just submitted to highlight
      setJustSubmittedShift(body.date || body.start_date);
      setTimeout(() => setJustSubmittedShift(null), 5000); // Clear highlight after 5s
      setShiftNotes("");
      setExcludedDays([]);
      fetchData();
    } catch (e) { toast.error(e.message); }
  };

  const toggleExcludedDay = (dayIndex) => {
    setExcludedDays(prev => 
      prev.includes(dayIndex) 
        ? prev.filter(d => d !== dayIndex) 
        : [...prev, dayIndex]
    );
  };

  const handleClearShiftRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/shifts/my-requests/clear`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) { toast.success("Shift requests cleared"); fetchData(); }
    } catch (e) { toast.error(e.message); }
  };

  const handleClearTimeOffRequests = async () => {
    try {
      const res = await fetch(`${API_URL}/api/my-time-off/clear`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) { toast.success("Time off requests cleared"); fetchData(); }
    } catch (e) { toast.error(e.message); }
  };

  const handleExport = async (exportType) => {
    try {
      const start = format(exportStartDate, 'yyyy-MM-dd');
      const end = format(exportEndDate, 'yyyy-MM-dd');
      window.open(`${API_URL}/api/export/my-report?start_date=${start}&end_date=${end}&format=${exportType}`, '_blank');
      setShowExportDialog(false);
    } catch (e) { toast.error(e.message); }
  };

  const handleLogout = async () => {
    try { await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' }); } catch (e) { /* silent */ }
    navigate('/');
  };

  const handleLeaveCompany = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/leave-company`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success(t("leftCompanySuccess") || "Successfully left company");
        navigate('/role-setup', { state: { user } });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to leave company");
      }
    } catch (e) { toast.error("Error leaving company"); }
    setShowLeaveDialog(false);
  };

  const handleSaveProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ display_name: displayName, job_title: jobTitle })
      });
      if (res.ok) {
        const updated = await res.json();
        setUser(updated);
        toast.success(t("profileUpdated") || "Profile updated");
        setShowProfileDialog(false);
      } else {
        toast.error("Failed to update profile");
      }
    } catch (e) { toast.error("Error updating profile"); }
  };

  const formatTime = (ts) => ts ? format(new Date(ts), 'h:mm a') : '';
  const getStatusBadge = (status) => ({ pending: "bg-yellow-100 text-yellow-800", approved: "bg-green-100 text-green-800", denied: "bg-red-100 text-red-800", assigned: "bg-blue-100 text-blue-800" }[status] || "bg-gray-100");

  return (
    <div className={`min-h-screen bg-background ${isRTL ? 'rtl' : 'ltr'}`}>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
 <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-2">

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"><MapPin className="w-4 h-4 text-primary-foreground" /></div>
           <span className="font-heading font-bold text-base sm:text-lg md:text-xl">{t("appName")}</span>
            {companyName && <span className="text-2xl md:text-3xl font-heading font-bold text-primary hidden md:inline">• {companyName}</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowExportDialog(true)} data-testid="export-report-btn">
              <Download className="w-4 h-4 mr-2" />{t("exportReport")}
            </Button>
            <LanguageSelector />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-primary text-primary-foreground text-sm">{(user?.display_name || user?.name)?.charAt(0) || 'U'}</AvatarFallback></Avatar>
                  <span className="hidden md:inline text-sm font-medium">{user?.display_name || user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
             <DropdownMenuContent
  align={isRTL ? "start" : "end"}
  className="w-48"
>
                <DropdownMenuItem className="text-muted-foreground">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowProfileDialog(true)}><User className="w-4 h-4 mr-2" />{t("profile")}</DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />{t("signOut")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {companyName && <div className="md:hidden text-center py-2 bg-accent/30 font-heading font-bold text-xl text-primary">{companyName}</div>}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Trial Banner for Employees - Always visible during trial */}
        {subscriptionStatus?.status === "trial" && (
          <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-yellow-800 font-semibold">{t("trialActive") || "Company Trial Active"}</p>
                <p className="text-yellow-700 text-sm">{t("trialEmployeeNote") || "Your employer's trial ends in"} {subscriptionStatus.days_left} {t("days") || "days"}{subscriptionStatus.hours_left > 0 ? `, ${subscriptionStatus.hours_left} ${t("hours") || "hours"}` : ''}</p>
              </div>
            </div>
          </div>
        )}
        {subscriptionStatus?.status === "active" && !bannerDismissed && (
          <div className="mb-6 p-3 rounded-xl bg-green-50 border border-green-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <p className="text-green-800 text-sm font-medium">Active Subscription: {subscriptionStatus.plan?.charAt(0).toUpperCase() + subscriptionStatus.plan?.slice(1)} Plan</p>
            </div>
            <button onClick={dismissBanner} className="text-green-600 hover:text-green-800 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <Tabs defaultValue="checkin" className="space-y-6">
     <TabsList
  className="flex w-full gap-1 overflow-x-auto whitespace-nowrap flex-nowrap px-1"
  style={{ WebkitOverflowScrolling: "touch" }}
>
           <TabsTrigger
  value="checkin"
  className="flex-shrink-0 px-3 py-1 text-xs sm:text-sm"
>
  {t("checkIn")}
</TabsTrigger>

<TabsTrigger
  value="schedule"
  className="flex-shrink-0 px-3 py-1 text-xs sm:text-sm"
>
  {t("schedule")}
</TabsTrigger>

<TabsTrigger
  value="timeoff"
  className="flex-shrink-0 px-3 py-1 text-xs sm:text-sm"
>
  {t("timeOffRequests")}
</TabsTrigger>

<TabsTrigger
  value="group"
  className="flex-shrink-0 px-3 py-1 text-xs sm:text-sm"
>
  {t("groupSchedule")}
</TabsTrigger>
          </TabsList>

          {/* Check-in Tab */}
          <TabsContent value="checkin">
            <div className="grid gap-8 md:grid-cols-12">
              <div className="md:col-span-7">
                <Card className="border-border shadow-card overflow-hidden">
                  <CardContent className="p-8 md:p-12">
                    {/* Shift Context Banner */}
                    {todayShift && (
                      <div className="mb-6 p-3 rounded-lg bg-primary/5 border border-primary/20 text-center">
                        <div className="flex items-center justify-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-primary" />
                          <span className="font-medium">{t("todayShift") || "Today's shift"}: {todayShift.start_time} - {todayShift.end_time}</span>
                        </div>
                      </div>
                    )}
{geofenceStatus?.geofencing_enabled && (
  <div className="mb-6 p-4 rounded-lg border bg-muted/30">
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div>
        <p className="font-medium">{t("workLocationStatus") || "Work location status"}</p>
        <p className="text-sm text-muted-foreground">
          {isCheckingGeofence
            ? (t("checkingLocationStatus") || "Checking location status...")
            : (geofenceStatus?.message || "")}
        </p>
      </div>

      <Badge
        variant="outline"
        className={
          geofenceStatus?.can_check_in
            ? "border-green-300 text-green-700"
            : "border-red-300 text-red-700"
        }
      >
        {geofenceStatus?.can_check_in
          ? (t("insideAllowedArea") || "Inside allowed area")
          : (t("outsideAllowedArea") || "Outside allowed area")}
      </Badge>
    </div>

    {!geofenceStatus?.has_assignments && (
      <p className="text-sm text-destructive mt-3">
        {t("geofenceNoAssignment") || "Your employer must assign a work location first."}
      </p>
    )}

    {Array.isArray(geofenceStatus?.assigned_locations) &&
      geofenceStatus.assigned_locations.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-sm font-medium">{t("assignedLocations") || "Assigned locations"}</p>
          <div className="space-y-2">
            {geofenceStatus.assigned_locations.map((loc) => (
              <div key={loc.location_id} className="p-3 rounded-lg bg-background border">
                <p className="font-medium text-sm">{loc.name}</p>
                <p className="text-xs text-muted-foreground">{loc.address}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {(t("allowedRadius") || "Allowed radius")}: {loc.radius_meters}m
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
  </div>
)}
                    <div className="flex items-center justify-center mb-8">
                      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${isCheckedIn ? "bg-accent text-accent-foreground" : "bg-secondary text-secondary-foreground"}`}>
                        {isCheckedIn ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        <span className="font-medium text-sm">{isCheckedIn ? t("currentlyCheckedIn") : t("notCheckedIn")}</span>
                      </div>
                    </div>
                    <div className="relative flex justify-center mb-8">
                      {isCheckedIn && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="w-48 h-48 rounded-full bg-accent/30 animate-pulse-ring"></div></div>}
                   <div
  onMouseEnter={() => setIsHovered(true)}
  onMouseLeave={() => setIsHovered(false)}
  onClick={() => {
    if (isCheckedIn) {
      handleCheckOut();
      return;
    }

    if (geofenceStatus?.geofencing_enabled && !geofenceStatus?.can_check_in) {
      toast.error(
        geofenceStatus?.message ||
          (t("geofenceOutside") || "You are outside the allowed check-in area.")
      );
      return;
    }

    handleCheckIn();
  }}
  className="flex flex-col items-center justify-center select-none"
>
  <div
    className={`
       relative
  w-[75vw]
  max-w-[200px]
  aspect-square
  rounded-full
  bg-gray-100
  border border-gray-200
  shadow-[0_6px_18px_rgba(0,0,0,0.08)]
  transition-all duration-200
  ${!isCheckedIn && geofenceStatus?.geofencing_enabled && !geofenceStatus?.can_check_in ? "cursor-not-allowed opacity-60" : "cursor-pointer active:scale-95 hover:scale-105"}
    `}
  >
    {/* Full Ring */}
    <svg
      viewBox="0 0 100 100"
      className="absolute inset-0 w-full h-full"
    >
      <circle
        cx="50"
        cy="50"
        r="45"
        fill="none"
        stroke={
  isCheckedIn
    ? (!isTouchDevice && isHovered ? "#ef4444" : "#22c55e")
    : (!isTouchDevice && isHovered ? "#22c55e" : "#ef4444")
}
        strokeWidth="3"
        strokeLinecap="round"
        className="transition-colors duration-300"
      />
    </svg>
    

    {/* Fingerprint */}
    <div className="absolute inset-0 flex items-center justify-center">
      <FingerprintIcon
className={`w-16 h-16 transition-colors duration-300 ${
  isCheckedIn
    ? (!isTouchDevice && isHovered ? "text-red-500" : "text-green-500")
    : (!isTouchDevice && isHovered ? "text-green-500" : "text-red-500")
}`}
      />
    </div>
  </div>

  {/* Label */}
  <span
    className={`mt-6 text-2xl font-semibold transition-colors duration-300 ${
  isCheckedIn
    ? (!isTouchDevice && isHovered ? "text-red-600" : "text-green-600")
    : (!isTouchDevice && isHovered ? "text-green-600" : "text-red-600")
}`}
  >
    {isCheckedIn ? t("checkOut") : t("checkIn")}
  </span>
</div> 
</div>
                    
                    {/* Microcopy */}
                    <div className="text-center space-y-2">
                      {currentLocation ? <div className="inline-flex items-center gap-2 text-muted-foreground"><Navigation className="w-4 h-4" /><span className="font-mono text-sm">{currentLocation.latitude.toFixed(6)}, {currentLocation.longitude.toFixed(6)}</span></div>
                        : locationError ? <p className="text-destructive text-sm">{locationError}</p> : <p className="text-muted-foreground text-sm">{t("gettingLocation")}</p>}
                      <p className="text-xs text-muted-foreground">
                        {isCheckedIn 
                          ? (t("checkOutMicrocopy") || "Click to record your departure time and location")
                          : (t("checkInMicrocopy") || "Your GPS location will be recorded for attendance verification")}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Help Entry Point */}
                <button 
                  onClick={() => setShowHelpDialog(true)}
                  className="mt-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
                >
                  <HelpCircle className="w-4 h-4" />
                  {t("howCheckInWorks") || "How check-in works"}
                </button>
              </div>
              <div className="md:col-span-5">
                <Card className="border-border shadow-card">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading text-lg">{t("activityHistory")}</CardTitle>
                        {isToday(selectedDate) && <Badge variant="outline" className="mt-1 text-xs">{t("today") || "Today"}</Badge>}
                      </div>
                      <Popover><PopoverTrigger asChild><Button variant="outline" size="sm" className="h-9 gap-2"><CalendarIcon className="w-4 h-4" />{format(selectedDate, 'MMM d')}</Button></PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end"><Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} /></PopoverContent></Popover>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {history.length === 0 ? (
                      <div className="text-center py-8">
                        <Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground text-sm font-medium">{t("noActivity")}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t("noActivityHelp") || "Check-ins and check-outs will appear here"}</p>
                      </div>
                    ) : (
                      <div className="space-y-3">{history.map((r, i) => (
                        <div key={r.check_id || i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${r.type === 'check_in' ? 'bg-accent' : 'bg-secondary'}`}>{r.type === 'check_in' ? <CheckCircle2 className="w-4 h-4 text-primary" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm">{r.type === 'check_in' ? t("checkedIn") : t("checkedOut")}</p>
                              {r.is_late && <Badge variant="destructive" className="text-xs">{t("late")}</Badge>}
                              {r.flag_reason && r.flag_reason.includes("ip") && <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">{t("flaggedIP") || "IP Flag"}</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground font-mono truncate">{r.latitude?.toFixed(4)}, {r.longitude?.toFixed(4)}</p>
                            {r.late_reason && <p className="text-xs text-muted-foreground mt-1">↳ {r.late_reason}</p>}
                          </div>
                          <span className="text-sm text-muted-foreground font-mono">{formatTime(r.timestamp)}</span>
                        </div>))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            {/* Permanent Schedule Display (Salary Companies) */}
            {companyPayType === "salary" && permanentSchedule.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    {t("myPermanentSchedule") || "My Weekly Schedule"}
                  </CardTitle>
                  <CardDescription>{t("permanentScheduleDesc") || "Your regular working hours set by your employer"}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2">
                    {DAY_NAMES.map((day, idx) => {
                      const daySchedule = permanentSchedule.find(s => s.day_of_week === idx);
                      return (
                        <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${daySchedule?.is_working_day ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30'}`}>
                          <span className="font-medium">{t(day.toLowerCase()) || day}</span>
                          {daySchedule?.is_working_day ? (
                            <div className="flex items-center gap-2 text-sm">
                              <Clock className="w-4 h-4 text-primary" />
                              <span>{daySchedule.start_time} - {daySchedule.end_time}</span>
                            </div>
                          ) : (
                            <Badge variant="secondary">{t("dayOff") || "Day Off"}</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Shift Request Form */}
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>{companyPayType === "salary" ? (t("requestScheduleAdjustment") || "Request Schedule Adjustment") : t("requestShift")}</CardTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <Button 
                      size="sm" 
                      variant={shiftRequestMode === "single" ? "default" : "outline"}
                      onClick={() => setShiftRequestMode("single")}
                    >Single Day</Button>
                    <Button 
                      size="sm" 
                      variant={shiftRequestMode === "range" ? "default" : "outline"}
                      onClick={() => setShiftRequestMode("range")}
                    >{t("dateRange") || "Date Range"}</Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {shiftRequestMode === "single" ? (
                    <div className="space-y-2">
                      <Label>{t("shiftDate")}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="mr-2 h-4 w-4" />{format(shiftDate, 'PPP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar mode="single" selected={shiftDate} onSelect={(d) => d && setShiftDate(d)} />
                        </PopoverContent>
                      </Popover>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t("startDate")}</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-sm">
                                <CalendarIcon className="mr-2 h-4 w-4" />{format(shiftDate, 'PP')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={shiftDate} onSelect={(d) => d && setShiftDate(d)} />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label>{t("endDate")}</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" className="w-full justify-start text-sm">
                                <CalendarIcon className="mr-2 h-4 w-4" />{format(shiftEndDate, 'PP')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar mode="single" selected={shiftEndDate} onSelect={(d) => d && setShiftEndDate(d)} />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t("excludeDays") || "Exclude Days"}</Label>
                        <div className="flex flex-wrap gap-2">
                          {dayNames.map((day, i) => (
                            <Button
                              key={i}
                              size="sm"
                              variant={excludedDays.includes(i) ? "destructive" : "outline"}
                              onClick={() => toggleExcludedDay(i)}
                              className="text-xs"
                            >{day}</Button>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{t("selectDaysToExclude") || "Click days to exclude from request"}</p>
                      </div>
                    </>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>{t("startTime")}</Label><Input type="time" value={shiftStartTime} onChange={(e) => setShiftStartTime(e.target.value)} /></div>
                    <div className="space-y-2"><Label>{t("endTime")}</Label><Input type="time" value={shiftEndTime} onChange={(e) => setShiftEndTime(e.target.value)} /></div>
                  </div>
                  <div className="space-y-2"><Label>{t("reasonDetails")}</Label><Textarea placeholder={t("reasonDetailsPlaceholder")} value={shiftNotes} onChange={(e) => setShiftNotes(e.target.value)} rows={2} /></div>
                  <Button onClick={handleShiftRequest} className="w-full">{t("submitRequest")}</Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t("shiftRequests")}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{t("shiftRequestsHelp") || "Track your requests and employer responses"}</p>
                  </div>
                  {myShiftRequests.length > 0 && <Button variant="ghost" size="sm" onClick={handleClearShiftRequests} className="text-muted-foreground hover:text-destructive">{t("clearAll") || "Clear All"}</Button>}
                </CardHeader>
                <CardContent>
                  {myShiftRequests.length === 0 ? <div className="text-center py-8"><Clock className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground text-sm font-medium">{t("noShiftRequests")}</p><p className="text-xs text-muted-foreground mt-1">{t("noShiftRequestsHelp") || "Submit a request using the form on the left"}</p></div>
                    : <div className="space-y-3">{myShiftRequests.slice(0, 5).map((s) => (
                      <div key={s.shift_id} className="p-3 rounded-xl bg-muted/50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{s.date}</span>
                          <Badge className={getStatusBadge(s.status)}>{t(s.status)}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{s.start_time} - {s.end_time}</p>
                        {s.employer_notes && (
                          <div className="mt-2 p-2 bg-accent/50 rounded text-xs">
                            <span className="font-medium">{t("employerNotes") || "Notes"}:</span> {s.employer_notes}
                          </div>
                        )}
                      </div>))}
                    </div>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Time Off Tab */}
          <TabsContent value="timeoff">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><CalendarOff className="w-5 h-5" />{t("requestTimeOff")}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {/* Single/Range Toggle */}
                  <div className="flex gap-2">
                    <Button size="sm" variant={timeOffMode === "single" ? "default" : "outline"} onClick={() => setTimeOffMode("single")}>Single Day</Button>
                    <Button size="sm" variant={timeOffMode === "range" ? "default" : "outline"} onClick={() => setTimeOffMode("range")}>{t("dateRange") || "Date Range"}</Button>
                  </div>
                  
                  {timeOffMode === "single" ? (
                    <div className="space-y-2"><Label>{t("selectDate")}</Label>
                      <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(timeOffDate, 'PPP')}</Button></PopoverTrigger>
                        <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={timeOffDate} onSelect={(d) => d && setTimeOffDate(d)} /></PopoverContent></Popover>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2"><Label>{t("startDate")}</Label>
                          <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start text-sm"><CalendarIcon className="mr-2 h-4 w-4" />{format(timeOffDate, 'PP')}</Button></PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={timeOffDate} onSelect={(d) => d && setTimeOffDate(d)} /></PopoverContent></Popover>
                        </div>
                        <div className="space-y-2"><Label>{t("endDate")}</Label>
                          <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start text-sm"><CalendarIcon className="mr-2 h-4 w-4" />{format(timeOffEndDate, 'PP')}</Button></PopoverTrigger>
                            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={timeOffEndDate} onSelect={(d) => d && setTimeOffEndDate(d)} /></PopoverContent></Popover>
                        </div>
                      </div>
                      <div className="space-y-2"><Label>{t("excludeDays") || "Exclude Days"}</Label>
                        <div className="flex flex-wrap gap-2">
                          {dayNames.map((day, i) => (
                            <Button key={i} size="sm" variant={timeOffExcludedDays.includes(i) ? "destructive" : "outline"} onClick={() => toggleTimeOffExcludedDay(i)} className="text-xs">{day}</Button>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">{t("selectDaysToExclude") || "Click days to exclude"}</p>
                      </div>
                    </>
                  )}
                  
                  <div className="space-y-2"><Label>{t("reasonType")}</Label>
                    <Select value={timeOffReason} onValueChange={setTimeOffReason}><SelectTrigger><SelectValue placeholder="Select a reason" /></SelectTrigger>
                      <SelectContent><SelectItem value="sick">{t("reasons.sick")}</SelectItem><SelectItem value="personal">{t("reasons.personal")}</SelectItem><SelectItem value="vacation">{t("reasons.vacation")}</SelectItem><SelectItem value="family_emergency">{t("reasons.family_emergency")}</SelectItem><SelectItem value="appointment">{t("reasons.appointment")}</SelectItem><SelectItem value="other">{t("reasons.other")}</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>{t("reasonDetails")}</Label><Textarea placeholder={t("reasonDetailsPlaceholder")} value={timeOffDetails} onChange={(e) => setTimeOffDetails(e.target.value)} rows={3} /></div>
                  <Button onClick={handleTimeOffSubmit} disabled={isSubmittingTimeOff || !timeOffReason} className="w-full">{isSubmittingTimeOff ? t("loading") : t("submitRequest")}</Button>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>{t("timeOffRequests")}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{t("timeOffRequestsHelp") || "Your submitted requests and their status"}</p>
                  </div>
                  {timeOffRequests.length > 0 && <Button variant="ghost" size="sm" onClick={handleClearTimeOffRequests} className="text-muted-foreground hover:text-destructive">{t("clearAll") || "Clear All"}</Button>}
                </CardHeader>
                <CardContent>
                  {timeOffRequests.length === 0 ? <div className="text-center py-8"><CalendarOff className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground text-sm font-medium">{t("noTimeOffRequests")}</p><p className="text-xs text-muted-foreground mt-1">{t("noTimeOffRequestsHelp") || "Submit a request using the form on the left"}</p></div>
                    : <div className="space-y-3">{timeOffRequests.map((r) => (
                      <div key={r.request_id} className="p-3 rounded-xl bg-muted/50">
                        <div className="flex items-center justify-between mb-2"><span className="font-medium">{r.date}</span><Badge className={getStatusBadge(r.status)}>{t(r.status)}</Badge></div>
                        <p className="text-sm text-muted-foreground">{t(`reasons.${r.reason_type}`)}</p>
                        {r.reason_details && <p className="text-xs text-muted-foreground mt-1 italic">&quot;{r.reason_details}&quot;</p>}
                      </div>))}
                    </div>}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Group Schedule Tab */}
          <TabsContent value="group">
            <EmployeeGroupSchedule />
          </TabsContent>
        </Tabs>
      </main>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={(open) => { setShowProfileDialog(open); if (open) { setDisplayName(user?.display_name || user?.name || ""); setJobTitle(user?.job_title || ""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("profile")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("displayNameLabel")}</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t("displayNamePlaceholder")} />
            </div>
            <div className="space-y-2">
              <Label>{t("jobTitle") || "Job Title"}</Label>
              <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder={t("jobTitlePlaceholder") || "e.g. Software Engineer"} />
            </div>
            <Button onClick={handleSaveProfile} className="w-full">{t("save")}</Button>
            <div className="border-t pt-4 mt-4">
              <Button variant="destructive" onClick={() => setShowLeaveDialog(true)} className="w-full">
                <DoorOpen className="w-4 h-4 mr-2" />{t("leaveCompany") || "Leave Company"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Company Confirmation Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">{t("leaveCompany") || "Leave Company"}</DialogTitle>
            <DialogDescription>{t("leaveCompanyConfirm") || "Are you sure you want to leave this company? This action cannot be undone."}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>{t("cancel")}</Button>
            <Button variant="destructive" onClick={handleLeaveCompany}>{t("leave") || "Leave"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent><DialogHeader><DialogTitle>{t("exportReport")}</DialogTitle><DialogDescription>Download your attendance report</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("startDate")}</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(exportStartDate, 'PP')}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={exportStartDate} onSelect={(d) => d && setExportStartDate(d)} /></PopoverContent></Popover></div>
              <div className="space-y-2"><Label>{t("endDate")}</Label><Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(exportEndDate, 'PP')}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={exportEndDate} onSelect={(d) => d && setExportEndDate(d)} /></PopoverContent></Popover></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => handleExport('csv')} variant="outline" className="h-12"><Download className="w-4 h-4 mr-2" />CSV</Button>
              <Button onClick={() => handleExport('pdf')} className="h-12"><Download className="w-4 h-4 mr-2" />PDF</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Late Reason Dialog */}
      <Dialog open={showLateReasonDialog} onOpenChange={setShowLateReasonDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">{t("lateReason") || "Late Check-in"}</DialogTitle>
            <DialogDescription>{t("enterLateReason") || "Please enter the reason for your late check-in"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea 
              value={lateReason} 
              onChange={(e) => setLateReason(e.target.value)} 
              placeholder={t("lateReasonPlaceholder") || "Enter reason..."} 
              rows={3} 
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => { setShowLateReasonDialog(false); setPendingCheckInLocation(null); }} className="flex-1">{t("cancel")}</Button>
              <Button onClick={handleLateReasonSubmit} className="flex-1">{t("submit")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help Dialog - How Check-in Works */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              {t("howCheckInWorks") || "How Check-in Works"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("helpStep1Title") || "GPS Location Capture"}</p>
                <p className="text-sm text-muted-foreground">{t("helpStep1Desc") || "Your location is recorded only at the moment you check in or out — never tracked in between."}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("helpStep2Title") || "Late Detection"}</p>
                <p className="text-sm text-muted-foreground">{t("helpStep2Desc") || "If you check in after your scheduled start time, you'll be marked as late and asked for a reason."}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium">{t("helpStep3Title") || "Check Out"}</p>
                <p className="text-sm text-muted-foreground">{t("helpStep3Desc") || "Don't forget to check out when you leave. If you forget, your status will reset automatically the next day."}</p>
              </div>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
              <Info className="w-4 h-4 inline mr-2" />
              {t("helpPrivacyNote") || "Your privacy matters. Location data is only captured during check-in/out actions."}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowHelpDialog(false)} className="w-full">{t("gotIt") || "Got it"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDashboard;
